import './WordPartDeletion.scss'
import { COPY_STRINGS } from '../../../../../constants/translatedCopy'
import get from 'lodash.get'
import React, { useEffect, useMemo, useRef } from 'react'
import Spot, {useSpot} from '../../../../Spot'
import useDidAttempt from '../../hooks/useDidAttempt'
import usePauseControl from '../../hooks/usePauseControl'
import usePhraseTypeIndexes from '../../hooks/usePhraseTypeIndexes'
import { phraseTypes } from '../../../../../constants/constants'
import { useSelector } from 'react-redux'
import { selectReaderManager } from '../../../ReaderManagerEvents/ReaderManagerSlice'
import useEndingSpeech from '../../hooks/useEndingSpeech'
import useIsListening from '../useIsListening'

let startTime = null

export default function WordPartDeletion(props) {

    const { speech, currentPageIndex, targetPart, phraseMetadata, transcript, isPlayingVerbalInstructions, startPhrase } = props
    const { paused, setWillHandlePause, pausePhrase, resumePhrase, doneHandlingPause } = props;
    
    const readerManagerStates = useSelector(selectReaderManager)
    const { contextStates, isCurrentStoryStarted } = readerManagerStates;

    const isListening = useIsListening()
    const { addToEndingSequence, hasPendingEndingSequence } = useEndingSpeech()
    const { didAttempt } = useDidAttempt({transcript})
    const { shouldHandlePause, finishHandlePause, passPauseControlBackToReaderManager } = usePauseControl({
        paused,
        setWillHandlePause,
        pausePhrase,
        resumePhrase,
        doneHandlingPause
    })

    const {
        isExamplePhraseOfThisType,
        isLastPhraseOfThisType,
        isLastPageOfStory,
    } = usePhraseTypeIndexes(currentPageIndex, phraseTypes.WORD_PART_DELETION)

    const didPlayInstruction = useRef(false)
    const didHandlePause = useRef(false)

    const originalWord = get(phraseMetadata, 'instructions.text', '')
    const omitPart = useMemo(() => {
        if(!originalWord || !targetPart) return ''
        return originalWord.replace(targetPart, '')
    }, [originalWord, targetPart])

    /**
     * initialize Spot, start with awake state
     */
    const {spotState, assets: { bark }} = useSpot(speech, "AWAKE")

    /**
     * handle when the phrase is started, setup the mid phrase timeout
     */
    useEffect(() => {
        if(didPlayInstruction.current) return // do not play again if we already played the instruction
        if(hasPendingEndingSequence) return // hold off from start if the previous ending speech is still playing
        if(!isPlayingVerbalInstructions){
            didPlayInstruction.current = true
            speech.stopListening()
            const intro = isExamplePhraseOfThisType ? 
                COPY_STRINGS.word_part_deletion.intro_instruction({word: originalWord, omit: omitPart, target: targetPart}) : 
                COPY_STRINGS.word_part_deletion.actual_instruction({word: originalWord, omit: omitPart})
            speech.speak({
                textToSpeak: intro,
                onEnd: () => {
                    startTime = Date.now()
                    setTimeout(() => {
                        startPhrase()
                    }, 300)
                }
            }, true)
        }
    }, [isPlayingVerbalInstructions, hasPendingEndingSequence])

    useEffect(() => {
        if(isListening){
            console.log("⏳ Time lapse from end of speech to listening", Date.now() - startTime)
        }
    }, [isListening])

    /**
     * handle when the phrase is paused, play the re-prompt
     */
    useEffect(() => {
        // if the phrase is paused, and we should handle the pause, and we haven't handled the pause yet
        if(paused && shouldHandlePause && !didHandlePause.current){
            handlePause()
            didHandlePause.current = true
        }
    }, [paused, shouldHandlePause, didHandlePause.current])

    /**
     * handle when a transcript is received, play the finish speech and pass the pause control back to reader manager
     */
    useEffect(()=>{
        if(didAttempt){
            passPauseControlBackToReaderManager() // pass the pause control back to reader manager so it can handle the next pause
            registerEndingSpeech() // register the ending speech for reader manager to play on end of phrase
        }
    }, [didAttempt])

    /**
     * handles the re-prompt speech and passing the pause control back to reader manager
     */
    const handlePause = () => {
        const silencePrompt = get(phraseMetadata, 'instructions')
        if (silencePrompt) {
            speech.speak({ 
                textToSpeak: COPY_STRINGS.word_part_deletion.prompt_to_try({word: originalWord, omit: omitPart}), 
                onEnd: () => {
                    startTime = Date.now()
                    passPauseControlBackToReaderManager() // pass the pause control back to reader manager so it can handle the next pause
                    finishHandlePause() // let reader manager know we are done handling this pause
                }
            });
        }
    }

    /**
     * register an ending speech for reader manager to play on end of phrase
     */
    const registerEndingSpeech = () => {
        addToEndingSequence({...bark})
        if(isExamplePhraseOfThisType){
            addToEndingSequence({textToSpeak: COPY_STRINGS.word_part_deletion.intro_end})
        }else if(isLastPhraseOfThisType){
            if(isLastPageOfStory){
                addToEndingSequence({textToSpeak: COPY_STRINGS.word_part_deletion.end})
            }else{
                addToEndingSequence({textToSpeak: COPY_STRINGS.word_part_deletion.end_with_more_activities})
            }
        }
    }

    /**
     * all we will see is Spot in the awaken state throughout the activity
     */
    return (
        <Spot spotState={spotState}/>
    )
}
